import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaCalculator } from 'react-icons/fa';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import { getAddRecipePageSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { useTranslation } from 'react-i18next';
import { normalizeIngredient, getSupportedUnits } from '../utils/unitConverter';
import AIChatPopup from '../components/AIChatPopup';
import AliasesEditor from '../components/AliasesEditor';
import LiveCheckPanel from '../components/LiveCheckPanel';
import MachineCheckResult from '../components/MachineCheckResult';
import { hasPremiumAccess } from '../config/subscriptions';
import PersonalRecipeAnalysis from '../components/PersonalRecipeAnalysis';

// Recipe-type → recommended default target_brix (°Bx). Chosen as the
// midpoint of each type's recommended band (see TYPE_BRIX_BAND below).
// Used only when creating a NEW recipe — the user's explicit input
// always wins. See handleTypeChange() below for the write-guard that
// prevents silent overwrites.
// Feb 2026: defaults & bands widened to reflect real SlushBook recipes
// (see PRD entry #52). Old sodavand default of 10°Bx was unrealistic —
// cola/lime slush lands around 20-25°Bx due to concentrated syrup.
const TYPE_DEFAULT_BRIX = {
  klassisk: 15,
  juice: 13,
  sodavand: 20,
  smoothie: 17,
  maelk: 16,
  kaffe: 14,
  cocktail: 20,
  sukkerfri: 3,
  sport: 8,
};

// Recommended Brix band per type — MUST match the bands used server-side
// in /admin/brix-outliers (see server.py::admin_brix_outliers::BANDS).
// Displayed inline under the Sukkergrad field so the user can immediately
// see whether their value looks reasonable for the chosen type.
// These are VEJLEDENDE hints (not hard errors) — the recipe still saves
// and the machine validator handles severity in 4 tiers (ideal /
// practical / warning / critical).
const TYPE_BRIX_BAND = {
  klassisk: [10, 20],
  juice: [8, 18],
  sodavand: [15, 25],
  smoothie: [12, 22],
  maelk: [12, 20],
  kaffe: [10, 18],
  cocktail: [15, 25],
  sukkerfri: [0, 6],
  sport: [4, 12],
};
const DEFAULT_TYPE = 'klassisk';

const AddRecipePage = ({ sessionId }) => {
  const { user, updateCompletedTours } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [canAdd, setCanAdd] = useState(true);
  const [recipesCount, setRecipesCount] = useState(0);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const [showVolumeInfo, setShowVolumeInfo] = useState(false);
  const [showBrixInfo, setShowBrixInfo] = useState(false);
  const [supportedUnits, setSupportedUnits] = useState(['ml', 'dl', 'l']);

  // Treat unauthenticated visitors and explicit "guest"-role users the same.
  const isGuestUser = !hasPremiumAccess(user);
  const [recipe, setRecipe] = useState({
    name: '',
    description: '',
    color: 'red',
    type: 'klassisk',
    alcohol_flag: false,
    base_volume_ml: 2700,
    target_brix: TYPE_DEFAULT_BRIX[DEFAULT_TYPE],
    tags: [],
    match_aliases: [],
    family: '',
    recommended_freeze_celsius: '',
    ingredients: [
      { name: '', category_key: '', quantity: '', unit: 'ml', role: 'required', brix: null }
    ],
    steps: [''],
    image_url: '/api/images/placeholder.jpg',
    is_published: false
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRightsConfirmed, setImageRightsConfirmed] = useState(false);
  
  // AI States
  const [showAICreatePopup, setShowAICreatePopup] = useState(false);
  const [showBrixAIPopup, setShowBrixAIPopup] = useState(false);
  const [isAICreateOpen, setIsAICreateOpen] = useState(false);
  
  // Machine list
  const [machines, setMachines] = useState([]);

  // Machine & Live Check States
  const [liveCheckData, setLiveCheckData] = useState(null);
  const [liveCheckLoading, setLiveCheckLoading] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [machineCheckData, setMachineCheckData] = useState(null);
  const [machineCheckLoading, setMachineCheckLoading] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Track whether the user has manually edited target_brix. As long as
  // brixManuallyEdited is false, changing the recipe type will silently
  // sync target_brix to the new type's default (TYPE_DEFAULT_BRIX). Once
  // the user has typed a custom Brix value, we ask for confirmation
  // before overwriting on subsequent type changes (see handleTypeChange).
  const [brixManuallyEdited, setBrixManuallyEdited] = useState(false);

  // Called when the user changes the Type/Gruppe dropdown. Applies the
  // per-type default target_brix, respecting the "user typed their own
  // Brix" guard. Never blindly overwrites — only prompts once and always
  // gives the user the escape hatch of keeping their value.
  const handleTypeChange = (newType) => {
    const defaultBrix = TYPE_DEFAULT_BRIX[newType];
    // If we don't have a default for this type or the type didn't change,
    // just update the type.
    if (defaultBrix === undefined || newType === recipe.type) {
      setRecipe((r) => ({ ...r, type: newType }));
      return;
    }
    const currentBrix = recipe.target_brix;
    const isEmpty = currentBrix === '' || currentBrix === null || currentBrix === undefined;
    const currentNum = parseFloat(currentBrix);
    // Auto-apply if the field is empty OR the user hasn't manually edited it.
    if (isEmpty || !brixManuallyEdited) {
      setRecipe((r) => ({ ...r, type: newType, target_brix: defaultBrix }));
      return;
    }
    // User has a custom value — confirm before overwriting.
    const ok = window.confirm(
      t(
        'addRecipe.confirmBrixOverwrite',
        `Sukkergrad er sat til ${currentNum}°Bx. Nulstil til ${defaultBrix}°Bx (standard for denne type)?\n\nKlik OK for at overskrive · Annullér for at beholde ${currentNum}°Bx.`
      )
    );
    if (ok) {
      setRecipe((r) => ({ ...r, type: newType, target_brix: defaultBrix }));
      setBrixManuallyEdited(false);  // reset so subsequent type changes flow again
    } else {
      // Keep the manual Brix, only change the type.
      setRecipe((r) => ({ ...r, type: newType }));
    }
  };

  // Detect whether the user has provided at least one fully-filled ingredient.
  // An empty/blank-only ingredient row should not trigger Live SlushCheck errors.
  const hasAnyFilledIngredient = recipe.ingredients.some(
    (ing) => (ing?.name || '').trim() && (ing?.category_key || '').trim() && String(ing?.quantity || '').trim()
  );
  const showLiveCheck = hasAnyFilledIngredient || hasAttemptedSave;

  useEffect(() => {
    checkLimits();
    // Fetch supported units based on language
    fetchSupportedUnits();
    // Fetch machines list
    fetchMachines();
  }, [sessionId, i18n.language]);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machine-catalog/list`);
      setMachines(response.data.machines || []);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchSupportedUnits = async () => {
    try {
      const lang = i18n.language || 'da';
      const response = await axios.get(`${API}/units/supported?language=${lang}`);
      setSupportedUnits(response.data.units);
    } catch (error) {
      console.error('Could not fetch supported units:', error);
      // Fallback to default Danish units
      setSupportedUnits(['ml', 'dl', 'l']);
    }
  };

  // Start tour if coming from RecipesPage
  useEffect(() => {
    if (hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.ADD_RECIPE, user) && isTourCompleted(TOUR_KEYS.RECIPES, user)) {
      console.log('[Tour] Starting add recipe tour...');
      setTimeout(() => {
        setCurrentTourStep(0);
      }, 1000);
    }
  }, [user]);

  const handleTourNext = () => {
    setCurrentTourStep(prev => prev + 1);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.ADD_RECIPE, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.ADD_RECIPE, API, updateCompletedTours);
    setCurrentTourStep(-1);
    toast.success(t('addRecipe.tourComplete', '🎉 Tour færdig! Du er klar til at bruge appen!'));
    // Optionally navigate back to recipes
    setTimeout(() => {
      navigate('/recipes');
    }, 2000);
  };


  const checkLimits = async () => {
    try {
      const response = await axios.get(`${API}/user/${sessionId}/limits`);
      setCanAdd(response.data.can_add_recipe);
      setRecipesCount(response.data.user_recipes_count || 0);
      // The backend signals "unlimited" with the magic limit_message token.
      setIsPro((response.data.limit_message || '').includes('__UNLIMITED_RECIPES__'));
      if (!response.data.can_add_recipe) {
        toast.error(t('addRecipe.limitReached'));
      }
    } catch (error) {
      console.error('Error checking limits:', error);
    }
  };

  const addIngredient = () => {
    setRecipe({
      ...recipe,
      ingredients: [
        ...recipe.ingredients,
        { name: '', category_key: '', quantity: '', unit: 'ml', role: 'required', brix: null }
      ]
    });
    // Scroll to bottom of ingredients section to show new ingredient
    setTimeout(() => {
      const ingredientsSection = document.querySelector('.ingredients-section');
      if (ingredientsSection) {
        const lastIngredient = ingredientsSection.querySelector('.ingredient-item:last-child');
        if (lastIngredient) {
          lastIngredient.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index][field] = value;
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const removeIngredient = (index) => {
    if (recipe.ingredients.length === 1) {
      toast.error('Du skal have mindst 1 ingrediens');
      return;
    }
    setRecipe({
      ...recipe,
      ingredients: recipe.ingredients.filter((_, i) => i !== index)
    });
  };

  const moveIngredientUp = (index) => {
    if (index === 0) return;
    const newIngredients = [...recipe.ingredients];
    [newIngredients[index - 1], newIngredients[index]] = [newIngredients[index], newIngredients[index - 1]];
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const moveIngredientDown = (index) => {
    if (index === recipe.ingredients.length - 1) return;
    const newIngredients = [...recipe.ingredients];
    [newIngredients[index], newIngredients[index + 1]] = [newIngredients[index + 1], newIngredients[index]];
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const addStep = () => {
    setRecipe({ ...recipe, steps: [...recipe.steps, ''] });
    // Scroll to bottom of steps section to show new step
    setTimeout(() => {
      const stepsSection = document.querySelector('.steps-section');
      if (stepsSection) {
        const lastStep = stepsSection.querySelector('textarea:last-of-type');
        if (lastStep) {
          lastStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          lastStep.focus();
        }
      }
    }, 100);
  };

  const updateStep = (index, value) => {
    const newSteps = [...recipe.steps];
    newSteps[index] = value;
    setRecipe({ ...recipe, steps: newSteps });
  };

  const removeStep = (index) => {
    if (recipe.steps.length === 1) {
      toast.error('Du skal have mindst 1 trin');
      return;
    }
    setRecipe({ ...recipe, steps: recipe.steps.filter((_, i) => i !== index) });
  };

  const moveStepUp = (index) => {
    if (index === 0) return;
    const newSteps = [...recipe.steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    setRecipe({ ...recipe, steps: newSteps });
  };

  const moveStepDown = (index) => {
    if (index === recipe.steps.length - 1) return;
    const newSteps = [...recipe.steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    setRecipe({ ...recipe, steps: newSteps });
  };

  const addTag = () => {
    if (tagInput.trim() && !recipe.tags.includes(tagInput.trim())) {
      setRecipe({ ...recipe, tags: [...recipe.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setRecipe({ ...recipe, tags: recipe.tags.filter(t => t !== tag) });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        toast.error('Billede må maks være 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return recipe.image_url;
    
    const formData = new FormData();
    formData.append('file', imageFile);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.image_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Kunne ikke uploade billede');
      return recipe.image_url;
    }
  };

  // AI Handler: Create recipe from description
  const handleAICreateRecipe = async (aiRecipe) => {
    try {
      // Normalize the type so we can look up a sane brix default. AI
      // often returns capitalised strings ("Klassisk"). Keys in
      // TYPE_DEFAULT_BRIX are lower-case.
      const aiType = (aiRecipe.type || '').toString().toLowerCase() || DEFAULT_TYPE;
      const normalisedType = TYPE_DEFAULT_BRIX[aiType] !== undefined ? aiType : DEFAULT_TYPE;
      const targetBrix = aiRecipe.brix ?? TYPE_DEFAULT_BRIX[normalisedType];
      // Map AI recipe to form state
      setRecipe({
        name: aiRecipe.name || '',
        description: aiRecipe.description || '',
        type: normalisedType,
        target_brix: targetBrix,
        base_volume_ml: aiRecipe.base_volume_ml || 2000,
        contains_alcohol: aiRecipe.contains_alcohol || false,
        tags: aiRecipe.tags || [],
        ingredients: (aiRecipe.ingredients || []).map(ing => ({
          name: ing.name,
          category_key: ing.category,
          quantity: ing.amount,
          unit: ing.unit || 'ml',
          role: ing.required ? 'required' : 'optional',
          brix: ing.brix
        })),
        steps: aiRecipe.procedure || []
      });
      // Treat the AI-provided (or defaulted) Brix as "manually chosen" so
      // subsequent type changes prompt before overwriting it.
      setBrixManuallyEdited(true);
      setShowAICreatePopup(false);
      toast.success('Opskrift indlæst fra AI! Gennemgå og gem.');
    } catch (error) {
      console.error('Error parsing AI recipe:', error);
      toast.error('Kunne ikke indlæse opskrift');
    }
  };

  // AI Handler: Insert Brix value
  const handleBrixAIInsert = (brixData) => {
    // Extract Brix value from AI response
    const brixMatch = brixData.match(/(\d+\.?\d*)\s*°?Bx/i);
    if (brixMatch) {
      setRecipe({ ...recipe, target_brix: parseFloat(brixMatch[1]) });
      setBrixManuallyEdited(true);
      setShowBrixAIPopup(false);
      toast.success(`Brix ${brixMatch[1]}°Bx indsat!`);
    } else {
      toast.info('Kunne ikke finde Brix værdi i svaret');
    }
  };

  // Run Live Check (debounced)
  const runLiveCheck = async () => {
    if (!recipe.base_volume_ml || recipe.base_volume_ml <= 0) {
      return;
    }

    // Don't run Live SlushCheck on an empty recipe — wait until the user has
    // typed at least one ingredient (or attempted to save). Otherwise the
    // panel would flag the brand-new editor with red errors which is a bad
    // first impression. See ux ticket: empty Live SlushCheck on mobile.
    if (!hasAnyFilledIngredient && !hasAttemptedSave) {
      setLiveCheckData(null);
      setLiveCheckLoading(false);
      return;
    }

    setLiveCheckLoading(true);
    try {
      // Calculate alcohol percentage from ingredients
      let totalAlcohol = 0;
      recipe.ingredients.forEach(ing => {
        if (ing.name && ing.name.toLowerCase().includes('vodka')) {
          totalAlcohol += (parseFloat(ing.quantity) || 0) * 0.4; // Assume 40%
        } else if (ing.name && ing.name.toLowerCase().includes('rom')) {
          totalAlcohol += (parseFloat(ing.quantity) || 0) * 0.4;
        }
        // Add more alcohol detection logic as needed
      });
      const alcoholPercent = (totalAlcohol / recipe.base_volume_ml) * 100;

      const response = await axios.post(`${API}/ai/live-check`, {
        recipe: {
          base_volume_ml: recipe.base_volume_ml,
          target_brix: recipe.target_brix,
          contains_alcohol: recipe.alcohol_flag,
          ingredients: recipe.ingredients
        },
        machine_id: selectedMachine,
        room_temp_c: 20
      });

      if (response.data.success) {
        setLiveCheckData(response.data);
      }
    } catch (error) {
      console.error('Live check error:', error);
    } finally {
      setLiveCheckLoading(false);
    }
  };

  // Run Machine Check
  const runMachineCheck = async () => {
    if (!selectedMachine || !recipe.base_volume_ml) {
      return;
    }

    setMachineCheckLoading(true);
    try {
      const response = await axios.post(`${API}/machine/check`, {
        machine_id: selectedMachine,
        total_volume_ml: recipe.base_volume_ml,
        brix: recipe.target_brix,
        alcohol_percent: recipe.alcohol_flag ? 4.0 : 0.0 // Simplified
      });

      if (response.data.ok) {
        setMachineCheckData(response.data);
      }
    } catch (error) {
      console.error('Machine check error:', error);
    } finally {
      setMachineCheckLoading(false);
    }
  };

  // Debounced live check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (recipe.base_volume_ml && recipe.target_brix) {
        runLiveCheck();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [recipe.base_volume_ml, recipe.target_brix, recipe.ingredients, recipe.alcohol_flag, selectedMachine]);

  // Trigger machine check when machine changes
  useEffect(() => {
    if (selectedMachine) {
      runMachineCheck();
    }
  }, [selectedMachine, recipe.base_volume_ml, recipe.target_brix]);

  // ── Live freeze-point beregning (Feb 2026) ────────────────────────────
  // Kalder den fælles motor via POST /api/recipe-analysis/freeze-point.
  // Én kilde til sandhed — bruges også af PersonalRecipeAnalysis og
  // /api/validate-recipe-machine. Debounced 400ms så vi ikke spammer
  // API'et mens brugeren skriver.
  const [freezeCalc, setFreezeCalc] = useState(null);
  // Eksplicit override af "auto/manual"-tilstand for freeze-feltet.
  // `null` = brug tolerance-heuristik. Sættes eksplicit ved checkbox-klik.
  const [freezeManualOverride, setFreezeManualOverride] = useState(null);
  useEffect(() => {
    if (!recipe.target_brix) {
      setFreezeCalc(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(`${API}/recipe-analysis/freeze-point`, {
          recipe: {
            target_brix: parseFloat(recipe.target_brix) || 0,
            alcohol_flag: !!recipe.alcohol_flag,
            alcohol_percentage: recipe.alcohol_percentage,
            recommended_freeze_celsius: recipe.recommended_freeze_celsius,
          },
        });
        setFreezeCalc(response.data);
      } catch (err) {
        console.warn('[FreezePoint] calc failed', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [recipe.target_brix, recipe.alcohol_flag, recipe.alcohol_percentage, recipe.recommended_freeze_celsius]);


  const submitRecipe = async (e) => {
    e.preventDefault();
    setHasAttemptedSave(true);
    
    if (!recipe.name || !recipe.description) {
      toast.error('Udfyld navn og beskrivelse');
      return;
    }

    if (!recipe.base_volume_ml || recipe.base_volume_ml <= 0) {
      toast.error('Basis volumen er obligatorisk og skal være større end 0');
      return;
    }

    if (recipe.ingredients.some(ing => !ing.name || !ing.category_key)) {
      toast.error('Alle ingredienser skal have navn og kategori');
      return;
    }

    if (recipe.steps.some(step => !step.trim())) {
      toast.error('Alle trin skal udfyldes');
      return;
    }

    setLoading(true);
    try {
      // Upload image first if exists
      const imageUrl = await uploadImage();
      
      // Normalize ingredients to ml before sending to backend
      const normalizedIngredients = recipe.ingredients.map(ing => {
        const normalized = normalizeIngredient({
          ...ing,
          quantity: parseFloat(ing.quantity),
          brix: ing.brix ? parseFloat(ing.brix) : null
        });
        return normalized;
      });

      // Anbefalet maskintemperatur: hvis "Beregn automatisk" er aktiv, hent
      // en frisk beregnet værdi fra backend LIGE FØR save. Fjerner alle
      // race-conditions med den debouncede live-freezeCalc-state og sikrer at
      // recommended_freeze_celsius altid er et tal i DB — så den vises på
      // opskriftskortet, detaljesiden og i editoren.
      const rfcRaw = recipe.recommended_freeze_celsius;
      const rfcIsEmpty = rfcRaw === '' || rfcRaw === null || rfcRaw === undefined;
      const computedAtRender = freezeCalc?.computed_machine_temp_c;
      const heuristicIsManual = !rfcIsEmpty && (
        computedAtRender == null || Math.abs(parseFloat(rfcRaw) - computedAtRender) > 0.1
      );
      const isManualAtSave = freezeManualOverride ?? heuristicIsManual;
      let rfcToStore;
      if (isManualAtSave) {
        rfcToStore = rfcIsEmpty ? null : parseFloat(rfcRaw);
      } else {
        try {
          const freshResp = await axios.post(`${API}/recipe-analysis/freeze-point`, {
            recipe: {
              target_brix: parseFloat(recipe.target_brix) || 0,
              alcohol_flag: !!recipe.alcohol_flag,
              alcohol_percentage: recipe.alcohol_percentage,
              recommended_freeze_celsius: null,
            },
          });
          rfcToStore = freshResp.data?.computed_machine_temp_c ?? null;
        } catch (err) {
          console.warn('[FreezePoint] sync compute failed, fallback to live state', err);
          rfcToStore = computedAtRender ?? null;
        }
      }

      const response = await axios.post(`${API}/recipes`, {
        session_id: sessionId,
        ...recipe,
        image_url: imageUrl,
        target_brix: parseFloat(recipe.target_brix),
        recommended_freeze_celsius: rfcToStore,
        ingredients: normalizedIngredients
      });
      toast.success(t('messages.success.created'));
      navigate(`/recipes/${response.data.id}`);
    } catch (error) {
      console.error('Error creating recipe:', error);
      if (error.response?.status === 403) {
        toast.error(t('addRecipe.limitReached'));
      } else {
        toast.error(t('addRecipe.createError', 'Kunne ikke oprette opskrift'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!canAdd) {
    return (
      <div className="max-w-2xl mx-auto fade-in">
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('addRecipe.limitReached')}</h2>
          <p className="text-gray-700 mb-4">
            {t('addRecipe.upgradePrompt')}
          </p>
          <Button onClick={() => navigate('/settings')}>{t('nav.settings')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="add-recipe-page">
      {/* Onboarding Tour */}
      <OnboardingTooltip
        steps={getAddRecipePageSteps()}
        currentStep={currentTourStep}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
        onFinish={handleTourFinish}
      />
      
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
      >
        <FaArrowLeft /> {t('common.back')}
      </button>

      <div>
        <h1 className="text-4xl font-bold mb-2">{t('addRecipe.title')}</h1>
        <p className="text-gray-600">{t('addRecipe.createYourOwn', 'Opret din egen slushice opskrift')}</p>
      </div>

      {/* Free-tier counter: shows "X af 2 gratis opskrifter brugt" for non-Pro users.
          Designed as a soft, informational chip — not an aggressive paywall. */}
      {!isPro && (
        <div
          className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
            canAdd
              ? 'bg-amber-50 border-amber-200'
              : 'bg-rose-50 border-rose-200'
          }`}
          data-testid="addrecipe-free-tier-counter"
        >
          <div className="text-sm">
            <span className="font-semibold text-gray-800">
              {recipesCount} {t('addRecipe.ofTwoFreeUsed', 'af 2 gratis opskrifter brugt')}
            </span>
            <span className="block text-xs text-gray-600 mt-0.5">
              {isGuestUser
                ? t('addRecipe.guestNote', 'Som gæst gemmes dine opskrifter privat — opret en konto for at synkronisere på tværs af enheder.')
                : t('addRecipe.freeNote', 'Opgradér til Light eller Pro for ubegrænsede opskrifter og deling.')}
            </span>
          </div>
          {!canAdd && (
            <a
              href="/profile"
              className="text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg whitespace-nowrap"
              data-testid="addrecipe-upgrade-link"
            >
              {t('addRecipe.upgradeShort', 'Opgradér')}
            </a>
          )}
        </div>
      )}

      {/* AI Create Recipe Button - Responsive Layout */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl p-4 shadow-md relative">
        <div className="flex items-center gap-3">
          <img src="/icons/ai/64.png" alt="AI" className="w-14 h-14 sm:w-12 sm:h-12 flex-shrink-0" />
          <div className="flex-1 min-w-0 text-white">
            <h3 className="text-lg sm:text-xl font-bold mb-1">{t('addRecipe.aiBoxTitle')}</h3>
            <p className="text-sm sm:text-base text-white/95 leading-tight">{t('addRecipe.aiBoxDescription')}</p>
          </div>
          <div className="flex-shrink-0">
            <Button
              type="button"
              onClick={() => setShowAICreatePopup(true)}
              className="bg-white text-purple-600 hover:bg-gray-100 font-bold px-5 py-2.5 text-base whitespace-nowrap rounded-lg"
            >
              {t('addRecipe.aiBoxButton')}
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={submitRecipe} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl font-bold">{t('addRecipe.basicInfo')}</h2>
          
          {/* Image Upload */}
          <div>
            <Label>{t('addRecipe.image')}</Label>
            <div className="mt-2 space-y-3">
              {imagePreview ? (
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FaPlus className="mx-auto text-gray-400 mb-2" size={32} />
                  <p className="text-gray-600 mb-2">{t('addRecipe.uploadImage')}</p>
                  <p className="text-xs text-gray-500">{t('addRecipe.imageLimit', 'Maks 5MB - JPG, PNG')}</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                id="recipe-image-upload"
                className="hidden"
              />

              {/* Live SlushCheck Panel */}
              {recipe.base_volume_ml > 0 && recipe.target_brix > 0 && (
                <div className="col-span-2 mt-4">
                  {showLiveCheck ? (
                    <LiveCheckPanel 
                      liveData={liveCheckData} 
                      loading={liveCheckLoading} 
                    />
                  ) : (
                    <div
                      className="bg-gradient-to-br from-cyan-50 to-blue-50 border-l-4 border-cyan-400 rounded-r-xl p-3 sm:p-4 flex items-start gap-3"
                      data-testid="live-check-empty-hint"
                      role="status"
                      aria-live="polite"
                    >
                      <span className="text-xl sm:text-2xl leading-none mt-0.5">⚡</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">
                          {t('live.title', 'Live SlushCheck™')}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                          {t(
                            'live.emptyHint',
                            'Live SlushCheck analyserer automatisk din opskrift, når du tilføjer ingredienser.'
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <label
                htmlFor="recipe-image-upload"
                className="block w-full cursor-pointer"
              >
                <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all">
                  <FaPlus />
                  <span>{imageFile ? t('addRecipe.changeImage') : t('addRecipe.chooseImage')}</span>
                </div>
                {imageFile && (
                  <p className="text-xs text-gray-600 mt-1 text-center">
                    {t('addRecipe.selectedFile')}: {imageFile.name}
                  </p>
                )}
              </label>
            </div>
          </div>
          
          <div data-tour="recipe-name">
            <Label>{t('addRecipe.name')} *</Label>
            <Input
              data-testid="recipe-name-input"
              value={recipe.name}
              onChange={(e) => setRecipe({...recipe, name: e.target.value})}
              placeholder={t('addRecipe.namePlaceholder')}
            />
          </div>

          <div>
            <Label>{t('addRecipe.description')} *</Label>
            <Textarea
              data-testid="recipe-description-input"
              value={recipe.description}
              onChange={(e) => setRecipe({...recipe, description: e.target.value})}
              placeholder={t('addRecipe.descriptionPlaceholder')}
              rows={3}
            />
            <p className="mt-1 text-xs text-gray-500" data-testid="description-bullet-hint">
              💡 Tip: Lav punktlister med <code className="px-1 bg-gray-100 rounded">Overskrift: * punkt * punkt</code>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-tour="recipe-type">
            {/* Type/Gruppe */}
            <div>
              <Label>{t('addRecipe.type')} *</Label>
              <select
                value={recipe.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md"
              >
                <option value="klassisk">{t('recipes.klassisk')}</option>
                <option value="juice">{t('recipes.juice')}</option>
                <option value="smoothie">{t('recipes.smoothie')}</option>
                <option value="sodavand">{t('recipes.sodavand')}</option>
                <option value="cocktail">{t('recipes.cocktail')}</option>
                <option value="kaffe">{t('recipes.kaffe')}</option>
                <option value="sport">{t('recipes.sport')}</option>
                <option value="sukkerfri">{t('recipes.sukkerfri')}</option>
                <option value="maelk">{t('recipes.maelk')}</option>
              </select>
            </div>

            {/* Sukkergrad */}
            <div>
              <Label className="flex items-center gap-2">
                {t('addRecipe.sugarLevel')}
                <button
                  type="button"
                  onClick={() => setShowBrixInfo(!showBrixInfo)}
                  className="text-cyan-600 hover:text-cyan-700 transition-colors"
                  title={t('addRecipe.clickForInfo')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </Label>
              <Input
                type="number"
                step="0.1"
                value={recipe.target_brix}
                onChange={(e) => {
                  setRecipe({...recipe, target_brix: e.target.value});
                  setBrixManuallyEdited(true);
                }}
                placeholder="13-15"
                data-testid="add-recipe-target-brix"
              />
              {/* Ideal-band hint — updates in sync with the Type selector.
                  Same bands used server-side by /admin/brix-outliers so the
                  user gets an early warning before their recipe would show
                  up in the outlier report. */}
              {TYPE_BRIX_BAND[recipe.type] && (() => {
                const [bmin, bmax] = TYPE_BRIX_BAND[recipe.type];
                const brixNum = parseFloat(recipe.target_brix);
                const outOfBand = !isNaN(brixNum) && (brixNum < bmin || brixNum > bmax);
                return (
                  <div
                    className={`text-xs mt-1 flex items-center gap-1 ${outOfBand ? 'text-amber-700' : 'text-gray-500'}`}
                    data-testid="add-recipe-brix-band-hint"
                  >
                    <span aria-hidden="true">🎯</span>
                    <span>
                      {t('addRecipe.brixIdealBand', 'Ideelt interval')}:{' '}
                      <strong className="tabular-nums">{bmin}–{bmax}°Bx</strong>
                      {outOfBand && (
                        <span className="ml-1">
                          — {t('addRecipe.brixOutOfBand', 'udenfor typens normale område')}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })()}
              {showBrixInfo && (
                <div className="text-sm mt-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-3 rounded-r animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="font-semibold text-cyan-900 mb-1 flex items-center gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>{t('addRecipe.sugarContent')}</span>
                  </p>
                  <p className="text-gray-700 text-xs leading-relaxed">
                    {t('addRecipe.brixInfo')}<br/>
                    <span className="text-gray-600">{t('addRecipe.brixRecommended')}</span><br/>
                    <a href="/brix-info" className="text-cyan-600 underline mt-1 inline-block" target="_blank">{t('addRecipe.readMoreBrix')}</a>
                  </p>
                </div>
              )}
            </div>

            {/* Basis Volumen - Stacks vertically on mobile, side-by-side on larger screens */}
            <div>
              <Label className="flex items-center gap-2">
                Basis Volumen (ml) <span className="text-red-500">*</span>
                <button
                  type="button"
                  onClick={() => setShowVolumeInfo(!showVolumeInfo)}
                  className="text-cyan-600 hover:text-cyan-700 transition-colors"
                  title="Klik for mere information"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </Label>
              <Input
                type="number"
                step="100"
                required
                value={recipe.base_volume_ml}
                onChange={(e) => setRecipe({...recipe, base_volume_ml: parseInt(e.target.value)})}
                placeholder="2700"
              />
              {showVolumeInfo && (
                <div className="text-sm mt-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-3 rounded-r animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="font-semibold text-cyan-900 mb-1 flex items-center gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>{t('addRecipe.requiredField')}</span>
                  </p>
                  <p className="text-gray-700 text-xs leading-relaxed">
                    {t('addRecipe.volumeDescription')}<br/>
                    <span className="text-gray-600">{t('addRecipe.volumeExample')}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Anbefalet maskintemperatur (°C) — auto-beregnes af den
                fælles motor via /api/recipe-analysis/freeze-point.
                Brugeren kan overstyre manuelt hvis nødvendigt.
                "Manuelt sat" bevares på recipe.recommended_freeze_celsius.
                Beregningen opdateres automatisk når Brix eller alkohol ændres. */}
            <div>
              <Label className="flex items-center gap-2">
                Anbefalet maskintemperatur (°C)
                <span className="text-gray-400 text-xs font-normal">
                  (beregnes automatisk)
                </span>
              </Label>
              {(() => {
                const stored = recipe.recommended_freeze_celsius;
                const storedIsSet = stored !== '' && stored !== null && stored !== undefined;
                const computed = freezeCalc?.computed_machine_temp_c;
                // Bestem tilstand: brugerens eksplicitte valg vinder,
                // ellers tolerance-heuristik (bruges primært ved genindlæsning).
                const heuristicIsManual = storedIsSet && (
                  computed == null || Math.abs(parseFloat(stored) - computed) > 0.1
                );
                const isManual = freezeManualOverride ?? heuristicIsManual;
                const displayVal = isManual
                  ? recipe.recommended_freeze_celsius
                  : (freezeCalc?.computed_machine_temp_c ?? '');
                return (
                  <>
                    <Input
                      type="number"
                      step="0.1"
                      value={displayVal}
                      disabled={!isManual}
                      onChange={(e) =>
                        setRecipe({ ...recipe, recommended_freeze_celsius: e.target.value })
                      }
                      placeholder={freezeCalc?.computed_machine_temp_c != null
                        ? `${freezeCalc.computed_machine_temp_c} (beregnet)`
                        : 'Beregnes automatisk...'}
                      data-testid="add-recipe-freeze-celsius"
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!isManual}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Sæt "Brug beregnet" til — nulstil manuelt felt
                            setFreezeManualOverride(false);
                            setRecipe({ ...recipe, recommended_freeze_celsius: '' });
                          } else {
                            // Skift til manuel — pre-udfyld med beregnet værdi
                            // og marker eksplicit at brugeren nu er i manuel mode.
                            setFreezeManualOverride(true);
                            setRecipe({
                              ...recipe,
                              recommended_freeze_celsius: String(
                                freezeCalc?.computed_machine_temp_c ?? '',
                              ),
                            });
                          }
                        }}
                        data-testid="add-recipe-freeze-use-computed"
                      />
                      <span>Beregn maskintemperaturen automatisk</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Når funktionen er aktiveret, beregner SlushBook automatisk den anbefalede
                      maskintemperatur ud fra Brix og alkoholprocent. Slå funktionen fra for at
                      angive en fast temperatur manuelt.
                    </p>
                  </>
                );
              })()}
            </div>

            {/* Machine Selection - Optional */}
            <div>
              <Label className="flex items-center gap-2">
                {t('machine.select_label', 'Slushmaskine')} (valgfrit)
              </Label>
              <select
                value={selectedMachine || ''}
                onChange={(e) => setSelectedMachine(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md"
              >
                <option value="">{t('machine.select_placeholder', '-- Vælg maskine --')}</option>
                {machines.length > 0 && machines.map((machine) => (
                  <option key={machine.name} value={machine.name}>
                    {machine.display_name || machine.name}
                  </option>
                ))}
              </select>
              {selectedMachine && (
                <p className="text-xs text-gray-500 mt-1">
                  ⚡ AI vil tjekke opskriften mod din maskine
                </p>
              )}
            </div>


            {/* Alcohol checkbox - Full width */}
            <div className="sm:col-span-2">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={recipe.alcohol_flag}
                  onChange={(e) => setRecipe({...recipe, alcohol_flag: e.target.checked})}
                  className="w-4 h-4"
                />
                {t('addRecipe.containsAlcohol')}
              </Label>
            </div>
          </div>

          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder={t('addRecipe.tagPlaceholder')}
              />
              <Button type="button" onClick={addTag}>
                {t('addRecipe.addTag')}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recipe.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <FaTrash size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <AliasesEditor
            value={recipe.match_aliases || []}
            onChange={(next) => setRecipe({ ...recipe, match_aliases: next })}
          />

          {/* Familie — valgfri grupperingsetiket inden for Type. */}
          <div>
            <Label htmlFor="recipe-family">
              Familie
              <span className="ml-2 text-xs font-normal text-gray-500">(valgfri)</span>
            </Label>
            <Input
              id="recipe-family"
              data-testid="recipe-family-input"
              value={recipe.family || ''}
              onChange={(e) => setRecipe({ ...recipe, family: e.target.value })}
              placeholder="fx Margarita, Piña Colada, Frappé, Protein"
            />
            <p className="text-xs text-gray-500 mt-1">
              Bruges til at gruppere opskrifter inden for samme Type. Lad være tom hvis ikke relevant.
            </p>
          </div>

          {/* Personlig maskineanalyse — vises kun for almindelige brugere.
              Redaktion (admin/editor) får den fulde 48-maskine analyse
              inde på EditRecipePage. Både denne og editorial-varianten
              deler den samme backend-motor (services/recipe_analysis.py). */}
          {recipe.target_brix && recipe.base_volume_ml
            && user?.role !== 'admin' && user?.role !== 'editor' && (
            <div className="md:col-span-2">
              <PersonalRecipeAnalysis
                recipe={recipe}
                userMachineName={user?.machine_name || null}
              />
            </div>
          )}
        </div>

        {/* Ingredients */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4 ingredients-section" data-tour="recipe-ingredients">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('addRecipe.ingredientsTitle')}</h2>
            <div className="flex gap-2">
              <div className="relative">
                <Button 
                  type="button" 
                  onClick={() => setShowBrixAIPopup(true)}
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white"
                  size="sm"
                >
                  <FaCalculator className="mr-2" /> Brix AI
                </Button>
              </div>
              <Button type="button" onClick={addIngredient} size="sm">
                <FaPlus className="mr-2" /> {t('addRecipe.addTag')}
              </Button>
            </div>
          </div>

          {recipe.ingredients.map((ingredient, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3 ingredient-item">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{t('addRecipe.ingredientNumber', { number: index + 1 })}</span>
                <div className="flex items-center gap-2">
                  {/* Move up button */}
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => moveIngredientUp(index)}
                      className="text-cyan-500 hover:text-cyan-700 p-1"
                      title={t('addRecipe.moveUp')}
                    >
                      <FaArrowUp size={14} />
                    </button>
                  )}
                  {/* Move down button */}
                  {index < recipe.ingredients.length - 1 && (
                    <button
                      type="button"
                      onClick={() => moveIngredientDown(index)}
                      className="text-cyan-500 hover:text-cyan-700 p-1"
                      title={t('addRecipe.moveDown')}
                    >
                      <FaArrowDown size={14} />
                    </button>
                  )}
                  {/* Delete button */}
                  {recipe.ingredients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder={t('addRecipe.namePlaceholder')}
                  value={ingredient.name}
                  onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                />
                <Input
                  placeholder={t('addRecipe.categoryPlaceholder')}
                  value={ingredient.category_key}
                  onChange={(e) => updateIngredient(index, 'category_key', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder={t('addRecipe.quantityPlaceholder')}
                  value={ingredient.quantity}
                  onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
                <select
                  value={ingredient.unit}
                  onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md"
                >
                  {supportedUnits.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
                <select
                  value={ingredient.role}
                  onChange={(e) => updateIngredient(index, 'role', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md"
                >
                  <option value="required">{t('addRecipe.required')}</option>
                  <option value="optional">{t('addRecipe.optional')}</option>
                </select>
                <Input
                  type="number"
                  placeholder={t('addRecipe.brixOptionalPlaceholder')}
                  value={ingredient.brix || ''}
                  onChange={(e) => updateIngredient(index, 'brix', e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4 steps-section">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('addRecipe.stepsTitle')}</h2>
            <Button type="button" onClick={addStep} size="sm">
              <FaPlus className="mr-2" /> {t('addRecipe.addStepButton')}
            </Button>
          </div>
          <p className="text-xs text-gray-500" data-testid="steps-bullet-hint">
            💡 Tip: Lav punktlister i et trin med <code className="px-1 bg-gray-100 rounded">Tekniske noter: * punkt * punkt</code>
          </p>

          {recipe.steps.map((step, index) => (
            <div key={index} className="flex gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {index + 1}
              </span>
              <Textarea
                value={step}
                onChange={(e) => updateStep(index, e.target.value)}
                placeholder={t('addRecipe.stepPlaceholder', { number: index + 1 })}
                rows={2}
                className="flex-1"
              />
              <div className="flex flex-col gap-1">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => moveStepUp(index)}
                    className="text-cyan-500 hover:text-cyan-700 p-1"
                    title={t('addRecipe.moveUp')}
                  >
                    <FaArrowUp size={14} />
                  </button>
                )}
                {index < recipe.steps.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveStepDown(index)}
                    className="text-cyan-500 hover:text-cyan-700 p-1"
                    title="Flyt ned"
                  >
                    <FaArrowDown size={14} />
                  </button>
                )}
              </div>
              {recipe.steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="text-red-500 hover:text-red-700 p-2"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Publish Toggle */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6" data-tour="recipe-public-toggle">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {isGuestUser
                  ? t('addRecipe.privateOnlyTitle', '🔒 Privat opskrift (kun dig)')
                  : (recipe.is_published ? t('addRecipe.publicRecipe', '🌍 Offentlig opskrift') : t('addRecipe.privateRecipe', '🔒 Privat opskrift'))}
              </h3>
              <p className="text-sm text-gray-600">
                {isGuestUser
                  ? t('addRecipe.guestPublishLocked', 'Som gæst gemmes opskriften privat. Deling og offentliggørelse er en Pro-funktion.')
                  : (recipe.is_published
                      ? t('addRecipe.publicDescription', 'Denne opskrift er synlig for alle brugere')
                      : t('addRecipe.privateDescription', 'Kun du kan se denne opskrift'))}
              </p>
            </div>
            <button
              type="button"
              disabled={isGuestUser}
              data-testid="addrecipe-publish-toggle"
              onClick={() => {
                if (isGuestUser) return;
                const newPublishedState = !recipe.is_published;
                setRecipe({...recipe, is_published: newPublishedState});
                // Reset confirmation when toggling off
                if (!newPublishedState) {
                  setImageRightsConfirmed(false);
                }
              }}
              title={isGuestUser ? t('addRecipe.guestPublishTooltip', 'Kræver Light- eller Pro-medlemskab for at offentliggøre opskrifter') : undefined}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                isGuestUser ? 'bg-gray-200 cursor-not-allowed opacity-70' : (recipe.is_published ? 'bg-green-500' : 'bg-gray-300')
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  (recipe.is_published && !isGuestUser) ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {/* Copyright Confirmation ONLY if image is uploaded */}
          {recipe.is_published && imageFile && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800 mb-2">
                  ⚠️ <strong>{t('addRecipe.imageWarning', 'Vigtigt om billede:')}</strong> {t('addRecipe.imageUploaded', 'Du har uploadet et billede til denne opskrift.')}
                </p>
                <p className="text-xs text-amber-700">
                  {t('addRecipe.ensureRights', 'Sørg for at du har rettighederne til billedet før du deler opskriften offentligt.')}
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={imageRightsConfirmed}
                  onChange={(e) => setImageRightsConfirmed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">
                  {t('addRecipe.imageRightsLabel')}
                  <span className="text-red-600">*</span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading || (recipe.is_published && imageFile && !imageRightsConfirmed)}
            data-testid="submit-recipe"
            data-tour="recipe-submit"
            className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
          >
            {loading ? t('addRecipe.creating', 'Opretter...') : t('addRecipe.createRecipeButton', 'Opret Opskrift')}
          </Button>
        </div>
      </form>

      {/* AI Create Recipe Popup */}
      <AIChatPopup
        isOpen={showAICreatePopup}
        onClose={() => setShowAICreatePopup(false)}
        endpoint="/ai/create-recipe"
        title="Lav opskrift med AI"
        placeholder="Beskriv din ønskede slush... f.eks. 'En sød jordbær slush med lidt citrus'"
        onInsert={handleAICreateRecipe}
        showInsertButton={true}
      />

      {/* Brix AI Popup */}
      <AIChatPopup
        isOpen={showBrixAIPopup}
        onClose={() => setShowBrixAIPopup(false)}
        endpoint="/ai/brix"
        title={t('addRecipe.brixAITitle', 'Beregn Brix med AI')}
        placeholder={t('addRecipe.brixAIPlaceholder', 'Beskriv dine ingredienser og mængder...')}
        onInsert={handleBrixAIInsert}
        showInsertButton={true}
      />
    </div>
  );
};

export default AddRecipePage;